import { useState, useEffect } from 'react';

/* Bump this whenever the algorithm changes — forces cache invalidation */
const ALGO_V = 4;
const cache  = {};

/* Promise-based version for preloading */
export function processImage(src) {
  const key = `v${ALGO_V}:${src}`;
  if (cache[key]) return Promise.resolve(cache[key]);

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imageData.data;

      for (let i = 0; i < d.length; i += 4) {
        const r = d[i];
        const g = d[i + 1];
        const b = d[i + 2];

        /* Euclidean distance from pure white (255,255,255) */
        const dr = 255 - r;
        const dg = 255 - g;
        const db = 255 - b;
        const distFromWhite = Math.sqrt(dr * dr + dg * dg + db * db);

        /* REMOVE_DIST: pixels this close to white → fully transparent.
           FADE_DIST:   pixels between REMOVE and FADE → smooth edge fade.
           Character colored/dark pixels have dist > FADE_DIST and are untouched. */
        const REMOVE_DIST = 40;
        const FADE_DIST   = 90;

        if (distFromWhite < REMOVE_DIST) {
          d[i + 3] = 0;
        } else if (distFromWhite < FADE_DIST) {
          /* t goes 0→1 from the remove boundary to the fade boundary */
          const t = (distFromWhite - REMOVE_DIST) / (FADE_DIST - REMOVE_DIST);
          d[i + 3] = Math.round(t * d[i + 3]);
        }
      }

      ctx.putImageData(imageData, 0, 0);
      const url = canvas.toDataURL('image/png');
      cache[key] = url;
      resolve(url);
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

/* Hook version for single-image use */
export function useBgRemoval(src) {
  const key = `v${ALGO_V}:${src}`;
  const [out, setOut] = useState(() => cache[key] || null);

  useEffect(() => {
    if (!src) return;
    if (cache[key]) { setOut(cache[key]); return; }
    processImage(src).then(setOut);
  }, [src]); // eslint-disable-line

  return out || src;
}
